import type { Language } from '@hero/schema';
import { agentCopy } from '../language/agent-copy';

/**
 * Copy for failures the backend NAMED, keyed by `ExceptionGlobal.errorId`.
 *
 * A declared `errorId` means somebody anticipated this exact failure and
 * it is the user's to fix — a phone already on the fleet, a plate
 * already registered, a CR the registry rejected. Those deserve the
 * sentence that names the thing and asks for the replacement.
 *
 * Before this map existed, `AgentFailureService` computed the `errorId`,
 * attached it to a Sentry tag, and then rendered
 * `publicErrorMessage('unknown')` — so every one of these arrived as
 * "Something went wrong, please try again". The user could not know that
 * the only problem was a phone number they had typed a minute earlier.
 *
 * Two rules for anything added here:
 *
 * 1. **Name the field and ask for the new value.** "That phone is
 *    already on your fleet — send me a different one" is actionable;
 *    "invalid input" is not. Every entry ends in something the user can
 *    type back.
 * 2. **No product name.** The same runtime answers as Hero and as
 *    Wathiqa Plus, so branding here labels a white-label line wrongly. Same
 *    rule as `publicErrorMessage`.
 *
 * An `errorId` that is absent from this map is not a bug — it falls back
 * to the generic code-based copy. Add one when the failure turns out to
 * be reachable by a user who could have fixed it themselves.
 */
const DECLARED_ERROR_IDS = [
  'member_user_already_in_org',
  // Reachable through a card that was staged while an account existed
  // and confirmed after it stopped existing. The tools for these steps
  // are withheld from callers with no account, so a card is the only
  // way in — and the answer is the same either way: the account step
  // comes first, and this continues right after it.
  'member_user_must_belong_to_org',
  'phone_number_is_duplicated',
  'user_already_have_account',
  'member_missing_driver_info',
  'member_phone_locked_after_sign_in',
  'member_driver_phone_taken',
  'member_phone_taken',
  'vehicle_owner_legal_name_is_required',
  'vehicle_plate_already_on_fleet',
  'vehicle_on_another_driver',
  // Reached when the owner-operator setup card arrives missing one of the
  // fields a وثيقة needs. Before this entry it rendered as the generic
  // "unexpected error" with no hint of which value to fix — the exact
  // dead-end this map exists to close. Names the four fields so the user
  // can re-send the missing card or type the value.
  'bayan_setup_owner_operator_invalid',
] as const;

type DeclaredErrorId = (typeof DECLARED_ERROR_IDS)[number];

const DECLARED = new Set<string>(DECLARED_ERROR_IDS);

const isDeclared = (errorId: string | undefined): errorId is DeclaredErrorId =>
  !!errorId && DECLARED.has(errorId);

/**
 * Localized, actionable copy for a declared `errorId`, or `undefined`
 * when this failure has no authored sentence and the caller should fall
 * back to {@link publicErrorMessage}.
 */
export const declaredErrorMessage = (
  errorId: string | undefined,
  language?: Language,
): string | undefined =>
  isDeclared(errorId) ? agentCopy(language ?? 'en', `errors.declared.${errorId}`) : undefined;

/** Whether {@link declaredErrorMessage} can answer this `errorId`. */
export const hasDeclaredErrorMessage = (errorId: string | undefined): boolean => isDeclared(errorId);
